import { getApp, getApps, initializeApp } from "firebase/app";
import {
	createUserWithEmailAndPassword,
	getAuth,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
	signOut,
	User,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore } from "firebase/firestore";
import firebaseConfig from "../firebaseConfig";
import {
	AppUserProfile,
	SabasebaSessionUser,
	SUPER_ADMIN_PERMISSIONS,
} from "../types/userPermissions";
import {
	createUserProfile,
	ensureSuperAdminProfile,
	fetchUserProfile,
} from "./userService";

const SECONDARY_APP_NAME = "SabasebaUserAdmin";
const BOOTSTRAP_ADMIN_EMAIL = "admin@sabaseba.com";
const BOOTSTRAP_ADMIN_PASSWORD = "sabaseba2026";

const splitDisplayName = (displayName: string) => {
	const parts = displayName.trim().split(/\s+/);
	return {
		firstName: parts[0] || "Usuario",
		lastName: parts.slice(1).join(" ") || "Sabaseba",
	};
};

export const buildSessionUser = (
	authUser: User,
	token: string,
	profile: AppUserProfile
): SabasebaSessionUser => {
	const { firstName, lastName } = splitDisplayName(profile.displayName);

	return {
		id: authUser.uid,
		uid: authUser.uid,
		email: profile.email,
		username: profile.displayName,
		firstName,
		lastName,
		role: profile.isSuperAdmin ? "Admin" : "User",
		token,
		isSuperAdmin: profile.isSuperAdmin,
		isActive: profile.isActive,
		permissions: profile.permissions,
	};
};

const getSecondaryAuth = () => {
	const existing = getApps().find((app) => app.name === SECONDARY_APP_NAME);
	const secondaryApp = existing || initializeApp(firebaseConfig, SECONDARY_APP_NAME);
	return getAuth(secondaryApp);
};

export const createAuthUserAccount = async (
	email: string,
	password: string
): Promise<string> => {
	const secondaryAuth = getSecondaryAuth();
	const credential = await createUserWithEmailAndPassword(
		secondaryAuth,
		email,
		password
	);
	await signOut(secondaryAuth);
	return credential.user.uid;
};

export const loginWithFirebase = async (
	email: string,
	password: string
): Promise<SabasebaSessionUser> => {
	const auth = getAuth(getApp());
	const firestore = getFirestore(getApp());
	let credential;

	try {
		credential = await signInWithEmailAndPassword(auth, email, password);
	} catch (error: any) {
		if (
			error?.code === "auth/user-not-found" &&
			email === BOOTSTRAP_ADMIN_EMAIL &&
			password === BOOTSTRAP_ADMIN_PASSWORD
		) {
			credential = await createUserWithEmailAndPassword(
				auth,
				BOOTSTRAP_ADMIN_EMAIL,
				BOOTSTRAP_ADMIN_PASSWORD
			);
		} else if (error?.code === "auth/invalid-credential") {
			throw new Error("Correo o contraseña incorrectos.");
		} else if (error?.code === "auth/wrong-password") {
			throw new Error("Correo o contraseña incorrectos.");
		} else if (error?.code === "auth/too-many-requests") {
			throw new Error("Demasiados intentos. Intenta más tarde.");
		} else {
			throw new Error("No fue posible iniciar sesión.");
		}
	}

	const token = await credential.user.getIdToken();
	let profile = await fetchUserProfile(firestore, credential.user.uid);

	if (!profile) {
		if (email === BOOTSTRAP_ADMIN_EMAIL) {
			profile = await ensureSuperAdminProfile(
				firestore,
				credential.user.uid,
				email
			);
		} else {
			await signOut(auth);
			throw new Error(
				"Tu cuenta no tiene acceso configurado. Contacta al administrador."
			);
		}
	}

	if (!profile.isActive) {
		await signOut(auth);
		throw new Error("Tu cuenta está desactivada.");
	}

	const effectiveProfile = profile.isSuperAdmin
		? { ...profile, permissions: SUPER_ADMIN_PERMISSIONS }
		: profile;

	return buildSessionUser(credential.user, token, effectiveProfile);
};

export const logoutFirebase = async (): Promise<void> => {
	const auth = getAuth(getApp());
	await signOut(auth);
};

export const provisionManagedUser = async (
	email: string,
	password: string,
	displayName: string,
	permissions: AppUserProfile["permissions"],
	createdBy?: string
): Promise<AppUserProfile> => {
	const firestore = getFirestore(getApp());
	const uid = await createAuthUserAccount(email, password);
	return createUserProfile(firestore, uid, {
		email,
		displayName,
		permissions,
		createdBy,
	});
};

const mapPasswordUpdateError = (error: any): string => {
	switch (error?.code) {
		case "functions/permission-denied":
			return "No tienes permiso para cambiar contraseñas.";
		case "functions/unauthenticated":
			return "Debes iniciar sesión nuevamente.";
		case "functions/invalid-argument":
			return "La contraseña debe tener al menos 6 caracteres.";
		case "functions/not-found":
			return "La función de administración no está desplegada en Firebase.";
		case "auth/too-many-requests":
			return "Demasiados intentos. Intenta más tarde.";
		default:
			return error?.message || "No fue posible actualizar la contraseña.";
	}
};

export const sendPasswordResetToUser = async (email: string): Promise<void> => {
	const auth = getAuth(getApp());
	await sendPasswordResetEmail(auth, email);
};

export const updateManagedUserPassword = async (
	uid: string,
	password: string
): Promise<void> => {
	if (!password || password.length < 6) {
		throw new Error("La contraseña debe tener al menos 6 caracteres.");
	}

	try {
		const functions = getFunctions(getApp());
		const callable = httpsCallable(functions, "adminUpdateUserPassword");
		await callable({ uid, password });
	} catch (error: any) {
		throw new Error(mapPasswordUpdateError(error));
	}
};
